import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './lib/firebase';
import LandingPage from "./pages/LandingPage";
import AssessmentPage from "./pages/AssessmentPage";
import ExportPage from "./pages/ExportPage";
import { Toaster } from "sonner";
import ConsolidatedAssessmentPage from "./pages/ConsolidatedAssessmentPage";
import MentorsPage from "./pages/MentorsPage";
import CandidateDashboard from "./pages/CandidateDashboard";

// Loading component
const LoadingSpinner = () => (
    <div className="min-h-screen bg-[#F9F9F6] flex items-center justify-center">
        <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#002FA7] border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-[#666666]">Loading...</p>
        </div>
    </div>
);

// Private Route Component with Firebase
const PrivateRoute = ({ children, allowedRoles }) => {
    const [user, setUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                setUser(firebaseUser);

                // Fetch user role from Firestore
                try {
                    const userDocRef = doc(db, 'users', firebaseUser.uid);
                    const userDoc = await getDoc(userDocRef);

                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        setUserRole(userData.role);

                        // Check if email is verified (optional)
                        if (!firebaseUser.emailVerified && userData.role !== 'admin') {
                            console.warn('Email not verified');
                            // You can redirect to verification page or show message
                        }
                    } else {
                        console.error('User document not found');
                        setUserRole(null);
                    }
                } catch (error) {
                    console.error('Error fetching user role:', error);
                    setUserRole(null);
                }
            } else {
                setUser(null);
                setUserRole(null);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (!user) {
        return <Navigate to="/" replace />;
    }

    if (allowedRoles && !allowedRoles.includes(userRole)) {
        console.warn(`Access denied. User role: ${userRole}, Required roles: ${allowedRoles}`);
        return <Navigate to="/" replace />;
    }

    return children;
};

// Public Route - redirects to dashboard if already logged in
const PublicRoute = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                setUser(firebaseUser);

                // Fetch user role
                try {
                    const userDocRef = doc(db, 'users', firebaseUser.uid);
                    const userDoc = await getDoc(userDocRef);
                    if (userDoc.exists()) {
                        setUserRole(userDoc.data().role);
                    }
                } catch (error) {
                    console.error('Error fetching user role:', error);
                }
            } else {
                setUser(null);
                setUserRole(null);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (user && userRole) {
        // Redirect to appropriate dashboard based on role
        if (userRole === 'admin') {
            return <Navigate to="/admin" replace />;
        } else if (userRole === 'mentor') {
            return <Navigate to="/mentor" replace />;
        } else if (userRole === 'candidate') {
            return <Navigate to="/dashboard" replace />;
        }
    }

    return children;
};

// Role-based redirect component
const RoleBasedRedirect = () => {
    const [user, setUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                setUser(firebaseUser);
                try {
                    const userDocRef = doc(db, 'users', firebaseUser.uid);
                    const userDoc = await getDoc(userDocRef);
                    if (userDoc.exists()) {
                        const role = userDoc.data().role;
                        setUserRole(role);

                        // Redirect based on role
                        if (role === 'admin') {
                            window.location.href = '/admin';
                        } else if (role === 'mentor') {
                            window.location.href = '/mentor';
                        } else if (role === 'candidate') {
                            window.location.href = '/dashboard';
                        }
                    }
                } catch (error) {
                    console.error('Error fetching user role:', error);
                }
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    if (loading) {
        return <LoadingSpinner />;
    }

    return <Navigate to="/" replace />;
};

function App() {
    return (
        <BrowserRouter>
            <div className="App">
                <Routes>
                    {/* Public Routes */}
                    <Route
                        path="/"
                        element={
                            <PublicRoute>
                                <LandingPage />
                            </PublicRoute>
                        }
                    />

                    {/* Role-based redirect */}
                    <Route path="/redirect" element={<RoleBasedRedirect />} />

                    {/* Candidate Routes */}
                    <Route
                        path="/dashboard"
                        element={
                            <PrivateRoute allowedRoles={['candidate']}>
                                <CandidateDashboard />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/mentors"
                        element={
                            <PrivateRoute allowedRoles={['candidate']}>
                                <MentorsPage />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/assess/:workExpId"
                        element={
                            <PrivateRoute allowedRoles={['candidate']}>
                                <AssessmentPage />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/assess/consolidated/:consolidationId"
                        element={
                            <PrivateRoute allowedRoles={['candidate']}>
                                <ConsolidatedAssessmentPage />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path="/export/:assessmentId"
                        element={
                            <PrivateRoute allowedRoles={['candidate']}>
                                <ExportPage />
                            </PrivateRoute>
                        }
                    />

                    {/*/!* Mentor Routes *!/*/}
                    {/*<Route*/}
                    {/*    path="/mentor"*/}
                    {/*    element={*/}
                    {/*        <PrivateRoute allowedRoles={['mentor']}>*/}
                    {/*            <MentorDashboard />*/}
                    {/*        </PrivateRoute>*/}
                    {/*    }*/}
                    {/*/>*/}

                    {/*/!* Admin Routes *!/*/}
                    {/*<Route*/}
                    {/*    path="/admin"*/}
                    {/*    element={*/}
                    {/*        <PrivateRoute allowedRoles={['admin']}>*/}
                    {/*            <AdminDashboard />*/}
                    {/*        </PrivateRoute>*/}
                    {/*    }*/}
                    {/*/>*/}

                    {/* Catch all route - redirect to home */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                <Toaster
                    position="top-right"
                    toastOptions={{
                        style: {
                            background: '#FFFFFF',
                            color: '#0A0A0A',
                            border: '1px solid #DCDCDA',
                        },
                        success: {
                            style: {
                                background: '#009944',
                                color: '#FFFFFF',
                            },
                        },
                        error: {
                            style: {
                                background: '#DC2626',
                                color: '#FFFFFF',
                            },
                        },
                    }}
                />
            </div>
        </BrowserRouter>
    );
}

export default App;